import { useState } from 'react'
import { Check, Copy, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { SUPPORTED_LANG_CODES, type SupportedLangCode } from '@/application/i18n/language'
import { localizedAbsoluteUrl } from '@/application/seo/site'
import type { Investigation } from '@/domains/types'
import { Badge } from '@/ui/components/common/Badge'
import { Button } from '@/ui/components/common/Button'
import { CollapsibleSection } from '@/ui/components/common/CollapsibleSection'

const LANGUAGE_NAME_KEYS: Record<SupportedLangCode, string> = {
  fr: 'translations.locale_fr',
  en: 'translations.locale_en',
  es: 'translations.locale_es',
}

/**
 * Read-only list of the public URL of an investigation in each language
 * (derived from its title in that language), with open and copy actions, and a
 * warning that renaming moves the address (the old one redirects). A language
 * whose slug equals the source-language slug is flagged as derived from the
 * source title: it has no translated title yet.
 */
export function InvestigationPublicUrls({
  slugs,
  sourceLocale,
}: {
  slugs?: Investigation['slugs']
  sourceLocale?: string
}) {
  const { t } = useTranslation()
  const [copiedLocale, setCopiedLocale] = useState<SupportedLangCode | null>(null)

  const entries = SUPPORTED_LANG_CODES.flatMap((locale) => {
    const slug = slugs?.[locale]
    return slug
      ? [{ locale, slug, address: localizedAbsoluteUrl(`/investigations/${slug}`, locale) }]
      : []
  })
  if (entries.length === 0) return null

  const sourceCode = SUPPORTED_LANG_CODES.find((code) => code === sourceLocale)
  const sourceSlug = sourceCode ? slugs?.[sourceCode] : undefined

  const copy = async (locale: SupportedLangCode, address: string) => {
    try {
      await navigator.clipboard.writeText(address)
      setCopiedLocale(locale)
      toast.success(t('investigation_form.public_url_copied'))
    } catch {
      toast.error(t('common.error'))
    }
  }

  return (
    <CollapsibleSection
      title={t('investigation_form.public_urls_title')}
      className="rounded-md border border-border px-3 py-1"
    >
      <ul className="space-y-2">
        {entries.map(({ locale, slug, address }) => (
          <li key={locale} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="w-24 shrink-0 font-medium">{t(LANGUAGE_NAME_KEYS[locale])}</span>
            <code className="min-w-0 break-all text-muted-foreground">{address}</code>
            {locale !== sourceLocale && sourceSlug !== undefined && slug === sourceSlug && (
              <Badge variant="outline">{t('investigation_form.public_url_source_fallback')}</Badge>
            )}
            <span className="ml-auto flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`${t('investigation_form.public_url_copy')} – ${t(LANGUAGE_NAME_KEYS[locale])}`}
                onClick={() => void copy(locale, address)}
              >
                {copiedLocale === locale ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
              <a
                href={address}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${t('investigation_form.public_url_open')} – ${t(LANGUAGE_NAME_KEYS[locale])}`}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 pb-2 text-sm text-muted-foreground">
        {t('investigation_form.public_urls_rename_warning')}
      </p>
    </CollapsibleSection>
  )
}
